import React from 'react';
import { useIntl } from 'react-intl';
import { Buildings, CheckCircle, MapPin, Clock, Hourglass, WarningCircle } from '@phosphor-icons/react';
import { describeDaysOpen } from '../../../../../utils/job-detail/describe-days-open';
import type { EmployerScoreTier } from '../../../../../utils/employer-score/mapScoreToTier';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { CompanyFollowButton } from '../../../../../components/company/CompanyFollowButton';
import { ApplyButton } from '../components';
import { Link } from '../../../../../components/Link';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { resolveSearchHitHostedLogoUrl } from '../../../../../utils/search/resolve-search-hit-company-display';
import type { DisplayJob } from '../../../../../types';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface BrandRailProps {
  displayJob: DisplayJob;
  panelColor: string;
  panelOn: string;
  /** Brand hue darkened enough to read clearly as text on the white "Apply" pill; see adjustBrandColor. */
  accentOnWhite: string;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  onTailorCv?: () => void;
  /** TEC-489, dark-launched: when provided, the apply button also offers "Autofill this application". */
  onAutofillApply?: () => void;
  /** Real, employer-quoted monthly-EUR pay (already formatted); only set for `salaryListingProvenance === 'employer_quote'`, never for a modelled estimate. TEC-509: surfaced directly above Apply, not just in the collapsible salary-comparison section further down, per Duunitori's own market survey (28% of Finnish job seekers wouldn't apply without visible pay). */
  realSalaryLabel?: string | null;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  companyId?: string | null;
  companyName?: string;
  isTyomarkkinatori?: boolean;
  location?: string;
  /** Set only when the company profile is linkable (shouldLinkCompanyProfile); see JobDetailPageV2.tsx. */
  companyProfileHref?: string;
  companyLinkTitle?: string;
  insightsCueLabel?: string;
  locale?: string;
  /**
   * TEC-512 editorial-rail redesign: gives the posted/last-seen/deadline stack a real visual
   * treatment (green "live" dot on last-seen, warm accent on the deadline when it's soon) instead
   * of plain stacked text; see the ticket's "Dates" design decision. Defaults to false so the
   * control experience (and every other BrandRail consumer) is byte-for-byte unchanged; only the
   * editorial-rail experiment variant passes true. Needs the raw ISO dates (lastSeenRaw /
   * applicationDeadlineRaw), not just the pre-formatted display strings, to compute freshness/
   * soon-ness itself; mirrors how JobDetailsSection.tsx already computes its own lastSeenFreshness
   * locally rather than having a caller pre-derive it.
   */
  enhancedDates?: boolean;
  lastSeenRaw?: string | null;
  applicationDeadlineRaw?: string | null;
  /** The company's Employer Score grade (A-E); shown as a pill under the company name, linking to the profile. Null when the company has no score or no linkable profile. */
  employerScoreTier?: EmployerScoreTier | null;
  /** Median days a listing for this role stays open (SalaryStatistics.medianDaysOpen); pairs with postedDaysAgo for the "usually open about N days" line. */
  typicalDaysOpen?: number | null;
}

/** "Soon" threshold for the deadline's warm accent; matches the plural day-count copy already used for postedDaysAgo. */
const DEADLINE_SOON_DAYS = 14;
/** How recent "last seen" needs to be for the live dot; a still-actively-recrawled listing, not merely posted recently. */
const LAST_SEEN_FRESH_HOURS = 48;

function daysUntil(dateStr: string): number {
  const diffMs = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

function hoursSince(dateStr: string): number {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  return diffMs / (60 * 60 * 1000);
}

/**
 * Left rail for the redesigned (V2) job detail page: logo, company identity (name, insights
 * link, location), a white "Apply now" pill, and posted/last-seen/expiry dates pinned to the
 * bottom. The full salary picture (estimates, comparisons, benefits) still lives in the
 * collapsible Salary Context section further down, where the real-vs-estimated distinction can
 * be shown properly; but TEC-509 surfaces the one number worth showing regardless, real
 * employer-quoted pay (never a modelled estimate), directly above Apply too, since that section
 * can be collapsed or scrolled past. See `realSalaryLabel` below.
 */
export function BrandRail({
  displayJob,
  panelColor,
  panelOn,
  accentOnWhite,
  applyUrl,
  isApplied,
  setAppliedJobState,
  onTailorCv,
  onAutofillApply,
  realSalaryLabel,
  formattedPostedDate,
  postedDaysAgo,
  formattedLastSeenDate,
  formattedApplicationDate,
  formattedEstimatedExpiryDate,
  companyId,
  companyName,
  isTyomarkkinatori = false,
  location,
  companyProfileHref,
  companyLinkTitle,
  insightsCueLabel,
  locale,
  enhancedDates = false,
  lastSeenRaw,
  applicationDeadlineRaw,
  employerScoreTier = null,
  typicalDaysOpen = null,
}: BrandRailProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();

  const applyBtnStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    color: accentOnWhite,
  };

  return (
    <div
      className="relative flex w-full flex-none flex-col items-center px-4 py-4 lg:px-5 lg:py-6 box-border lg:w-[210px] rounded-t-[24px] lg:rounded-l-[24px] lg:rounded-tr-none"
      style={{ background: panelColor, color: panelOn }}
    >
      {/* Desktop only: the whole identity block (logo, name, insights link, location, follow
          button, apply button) stays together and stays visible while scrolling, instead of
          just scrolling away with the rest of the rail. The lg:flex-1 wrapper below is the sticky
          div's own containing block, sized to end right above the dates block/divider (rather
          than at the bottom of the whole, taller rail) so the native sticky release triggers
          there instead of letting the stuck block scroll down over the dates. Mobile gets its
          own crossfade mini-header instead (JobDetailPageV2.tsx), matching the native app's
          job screen, since the rail isn't stretched full-height in the stacked mobile layout. */}
      <div className="w-full flex flex-col items-center lg:flex-1 lg:min-h-0">
      <div className="relative flex w-full flex-none flex-col items-center lg:sticky lg:top-6 lg:z-10">
        {companyId && !isTyomarkkinatori ? (
          // Desktop rail is a fixed 210px column with the logo centered in it, so a right-4
          // badge here would sit ~27px inside the logo's right edge; pushed further right
          // (into the rail's own outer padding, which has room to spare) so it clears the tile
          // instead of overlapping it, where its translucent onBrand fill goes invisible.
          <div className="absolute right-4 top-4 lg:-right-4 lg:top-3">
            <CompanyFollowButton compact compactSize={44} onBrand companyId={companyId} companyName={companyName} surface="job-page" />
          </div>
        ) : null}
        <div className="flex h-[72px] w-[72px] lg:h-[104px] lg:w-[104px] flex-none items-center justify-center rounded-[18px] lg:rounded-[22px] bg-white p-2.5 lg:p-3.5 box-border overflow-hidden">
          <CompanyLogo
            companyName={displayJob.companyId || displayJob.companyName || ''}
            initialsSourceName={displayJob.companyName ?? displayJob.companyId}
            registryCompanyFormDescription={displayJob.registryCompanyFormDescription}
            isTyomarkkinatori={displayJob.isTyomarkkinatori}
            hostedLogoUrl={resolveSearchHitHostedLogoUrl(displayJob as unknown as Record<string, unknown>)}
            variant="bare"
            size={104}
            loading="eager"
          />
        </div>

        <div className="mt-2.5 lg:mt-4 flex flex-col items-center gap-1 lg:gap-1.5 text-center w-full min-w-0">
          {companyName ? (
            <div className="[font-family:'Space_Grotesk',sans-serif] text-[15px] font-bold leading-tight break-words">
              {fixMojibake(companyName)}
            </div>
          ) : null}
          {companyProfileHref ? (
            <Link
              href={companyProfileHref}
              locale={locale}
              title={companyLinkTitle}
              className="inline-flex items-center gap-1 text-[11px] font-semibold opacity-90 hover:opacity-100 hover:underline underline-offset-2"
            >
              <Buildings weight="fill" className="h-3 w-3 shrink-0" aria-hidden />
              <span>{insightsCueLabel ?? formatMessage({ id: 'hit.companyInsights.cue', defaultMessage: 'Company insights', description: 'Short label under the employer name on a job card; the block links to the company profile' })}</span>
            </Link>
          ) : null}
          {employerScoreTier ? (() => {
            const scoreLabel = formatMessage({ id: 'jobDetailPageV2.employerScore.label', defaultMessage: 'Employer Score', description: 'Label next to the letter grade in the Employer Score pill under the company name in the job page brand rail' });
            const scoreAria = formatMessage(
              { id: 'jobDetailPageV2.employerScore.aria', defaultMessage: 'Employer Score {tier}. See the full company profile', description: 'Accessible label of the Employer Score pill in the job page brand rail; {tier} is a letter grade A to E' },
              { tier: employerScoreTier },
            );
            const pillClass = 'mt-1 inline-flex items-center gap-1.5 rounded-full border pl-0.5 pr-2.5 py-0.5 text-[11px] font-semibold';
            const pillStyle = { borderColor: 'color-mix(in srgb, currentColor 30%, transparent)', background: 'color-mix(in srgb, currentColor 10%, transparent)' } as const;
            const body = (
              <>
                <span
                  className="flex h-5 w-5 flex-none items-center justify-center rounded-full [font-family:'Space_Grotesk',sans-serif] text-[11.5px] font-bold leading-none"
                  style={{ background: panelOn, color: panelColor }}
                  aria-hidden
                >
                  {employerScoreTier}
                </span>
                <span>{scoreLabel}</span>
              </>
            );
            return companyProfileHref ? (
              <Link href={companyProfileHref} locale={locale} aria-label={scoreAria} title={scoreAria} className={`${pillClass} hover:brightness-110`} style={pillStyle}>
                {body}
              </Link>
            ) : (
              <span className={pillClass} style={pillStyle} aria-label={scoreAria}>
                {body}
              </span>
            );
          })() : null}
          {location ? (
            <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium opacity-90 break-words">
              <MapPin weight="fill" className="h-3 w-3 shrink-0" aria-hidden />
              <span>{fixMojibake(location)}</span>
            </div>
          ) : null}
        </div>

        {realSalaryLabel ? (
          <div
            className="mt-3 lg:mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-white/95 px-2.5 py-2 text-center"
            style={{ color: accentOnWhite }}
            aria-label={`${realSalaryLabel} ${formatMessage({
              id: 'jobDetailPageV2.realSalaryNote',
              defaultMessage: '(real pay, quoted by the employer)',
              description: "Note next to the prominent real-salary figure shown above the collapsible salary comparison section, clarifying it's from the employer and not an estimate",
            })}`}
          >
            <CheckCircle weight="fill" className="h-4 w-4 shrink-0" aria-hidden />
            <span className="text-[12.5px] font-bold leading-tight [font-family:'Space_Grotesk',sans-serif]" aria-hidden>
              {realSalaryLabel}
            </span>
          </div>
        ) : null}

        <div className={realSalaryLabel ? 'mt-2 w-full' : 'mt-3 lg:mt-5 w-full'}>
          <ApplyButton
            applyUrl={applyUrl}
            isApplied={isApplied}
            displayJob={displayJob}
            applyBtnStyle={applyBtnStyle}
            applyBtnHoverStyle={{ filter: 'brightness(0.96)' }}
            effectiveBrandColour={panelColor}
            onSetApplied={setAppliedJobState}
            onTailorCv={onTailorCv}
            onAutofillApply={onAutofillApply}
            compact
          />
        </div>
      </div>

      {/* Mobile only: marks where the rail's own (non-sticky) identity block ends, so
          JobDetailPageV2's IntersectionObserver knows when to fade in the crossfade
          mini-header. Placed after the sticky wrapper (which mobile ignores, no lg:sticky
          effect below the lg breakpoint) so it tracks the true end of the rail's content. */}
      <div id="brand-rail-mobile-sentinel" className="lg:hidden" aria-hidden="true" />
      </div>

      {(formattedPostedDate || formattedLastSeenDate || formattedApplicationDate || formattedEstimatedExpiryDate) ? (() => {
        const postedLine = formattedPostedDate
          ? formatMessage(
              { id: 'jobDetailPageV2.postedOn', defaultMessage: 'Posted {date}', description: 'Posted date shown in the redesigned job detail page brand rail' },
              { date: formattedPostedDate },
            ) +
            (postedDaysAgo != null
              ? ` · ${formatMessage({ id: 'jobDetailPageV2.daysAgo', defaultMessage: '{days, plural, one {# day ago} other {# days ago}}', description: 'Relative days-ago label' }, { days: postedDaysAgo })}`
              : '')
          : null;
        const lastSeenLine = formattedLastSeenDate
          ? formatMessage(
              { id: 'jobDetailPageV2.lastSeen', defaultMessage: 'Last seen {date}', description: 'Last-seen-in-crawl date shown in the redesigned job detail page brand rail' },
              { date: formattedLastSeenDate },
            )
          : null;
        const deadlineLine = formattedApplicationDate
          ? formatMessage(
              { id: 'jobDetailPageV2.deadline', defaultMessage: 'Deadline {date}', description: 'Application deadline shown in the redesigned job detail page brand rail' },
              { date: formattedApplicationDate },
            )
          : formattedEstimatedExpiryDate
            ? formatMessage(
                { id: 'jobDetailPageV2.estimatedExpiry', defaultMessage: 'Est. expiry {date}', description: 'Estimated listing expiry date shown in the redesigned job detail page brand rail' },
                { date: formattedEstimatedExpiryDate },
              )
            : null;
        const dateLines = [postedLine, lastSeenLine, deadlineLine].filter((line): line is string => Boolean(line));

        if (!enhancedDates) {
          return (
            <>
              {/* Desktop: one line per date, stacked (room to spare in the rail). */}
              <div className="hidden lg:flex flex-col items-center gap-1.5 pt-6 text-center text-[10.5px] font-semibold">
                {dateLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </div>
              {/* Mobile: joined onto as few lines as possible; the rail is full-width above the
                  content instead of a side column here, so stacking three separate lines cost real
                  scroll distance before the job content even starts. */}
              <div className="mt-3 lg:hidden w-full text-center text-[10.5px] font-semibold leading-relaxed">
                {dateLines.join(' · ')}
              </div>
            </>
          );
        }

        // TEC-512 editorial-rail visual treatment: a green "live" dot on last-seen (the
        // freshest signal we have that the listing is still real), posted date plain, and the
        // deadline in a warm accent when it reads as soon; same three lines, upgraded chrome.
        const isFresh = lastSeenRaw != null && hoursSince(lastSeenRaw) <= LAST_SEEN_FRESH_HOURS;
        const isDeadlineSoon =
          Boolean(formattedApplicationDate) && applicationDeadlineRaw != null && daysUntil(applicationDeadlineRaw) <= DEADLINE_SOON_DAYS;
        const daysLeft = isDeadlineSoon ? Math.max(0, daysUntil(applicationDeadlineRaw!)) : null;
        // "Open N days, this role usually stays open about M": context for an old posted date, from
        // the per-role median days-open the salary-stats run computes over closed listings.
        const daysOpen = describeDaysOpen(postedDaysAgo, typicalDaysOpen);
        const daysOpenLine = daysOpen
          ? daysOpen.tone === 'longer'
            ? formatMessage(
                { id: 'jobDetailPageV2.daysOpen.longer', defaultMessage: 'Open {days, plural, one {# day} other {# days}}, longer than the usual {typical} for this role', description: 'Brand rail line when a listing has been open well past the median for its role; {typical} is the median number of days' },
                { days: daysOpen.daysOpen, typical: daysOpen.typicalDays },
              )
            : formatMessage(
                { id: 'jobDetailPageV2.daysOpen.usual', defaultMessage: 'Open {days, plural, one {# day} other {# days}}. This role usually stays open about {typical} days', description: 'Brand rail line giving the median days-open for the role as context for how long this listing has been up' },
                { days: daysOpen.daysOpen, typical: daysOpen.typicalDays },
              )
          : null;

        return (
          <div className="w-full pt-4 lg:pt-6 border-t border-white/[0.18] flex flex-col items-start gap-1.5 text-left text-[10.5px] font-semibold">
            {lastSeenLine ? (
              <div className="flex items-center gap-1.5">
                <span
                  className={`h-1.5 w-1.5 flex-none rounded-full ${isFresh ? 'bg-[#8FE3A8]' : 'bg-white/35'}`}
                  aria-hidden
                />
                <span>{lastSeenLine}</span>
              </div>
            ) : null}
            {postedLine ? (
              <div className="flex items-center gap-1.5 opacity-90">
                <Clock className="h-3 w-3 flex-none opacity-75" aria-hidden />
                <span>{postedLine}</span>
              </div>
            ) : null}
            {daysOpenLine ? (
              <div className="flex items-start gap-1.5 opacity-90" style={daysOpen?.tone === 'longer' ? { color: '#F5C77A' } : undefined}>
                <Hourglass className="mt-[1px] h-3 w-3 flex-none opacity-75" aria-hidden />
                <span>{daysOpenLine}</span>
              </div>
            ) : null}
            {deadlineLine ? (
              <div className="flex items-center gap-1.5" style={isDeadlineSoon ? { color: '#F5C77A' } : undefined}>
                <WarningCircle className="h-3 w-3 flex-none opacity-75" aria-hidden />
                <span>
                  {deadlineLine}
                  {daysLeft != null
                    ? ` · ${formatMessage(
                        { id: 'jobDetailPageV2.daysLeft', defaultMessage: '{days, plural, one {# day left} other {# days left}}', description: 'Days remaining until the application deadline, shown in the editorial-rail redesign brand rail when the deadline is soon' },
                        { days: daysLeft },
                      )}`
                    : ''}
                </span>
              </div>
            ) : null}
          </div>
        );
      })() : null}
    </div>
  );
}
